import { Fragment, useEffect, useState } from "react";
import { MotiView } from "moti";
import { Pressable, StyleSheet, TextInput, View } from "react-native";
import { CheckCircle2, Mail, MessageSquareText } from "lucide-react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import CustomSwitch from "../../../../../components/common/general/CustomSwitch/CustomSwitch";
import CustomDropdown from "../../../../../components/common/general/CustomDropDown/CustomDropdown";
import CustomTextInput from "../../../../../components/common/general/CustomTextInput/CustomTextInput";
import { NudgeFrequency } from "@axio/graphql/generated/graphql";
import { colors, radii, spacing, typographyScale } from "../../../../../styles/design-system";
import { useOtpUiState } from "../hooks/useOtpUiState";

type DropdownOption = {
  label: string;
  value: string;
};

type EmailSmsPreferencesProps = {
  activeVerifiedSmsPhoneNumber?: string | null;
  emailEnabled: boolean;
  textMessagesEnabled: boolean;
  smsConsent: boolean;
  smsPhoneNumber: string;
  smsCountryCode?: string | null;
  isSmsPhoneVerified?: boolean;
  onPhoneVerificationSuccess: () => Promise<void> | void;
  frequencyValue: string;
  emailMaxPerDayValue: string;
  smsMaxPerDayValue: string;
  animationsEnabled: boolean;
  onUserInteraction: () => void;
  onEmailNotificationsToggle: (nextValue: boolean) => void;
  onTextMessagesToggle: (nextValue: boolean) => void;
  onSmsConsentChange: (nextValue: boolean) => void;
  onFrequencyChange: (nextValue: string) => void;
  onEmailMaxPerDayChange: (nextValue: string) => void;
  onSmsMaxPerDayChange: (nextValue: string) => void;
  onSmsPhoneNumberChange: (value: string) => void;
};

const SUBMENU_ANIMATION_DURATION_MS = 220;
const NOTIFICATION_DROPDOWN_WIDTH = 130;
const NO_LIMIT_PER_DAY_VALUE = "2147483647";
const FREQUENCY_OPTIONS: DropdownOption[] = [
  { label: "Daily", value: String(NudgeFrequency.Daily) },
  { label: "Every other day", value: String(NudgeFrequency.EveryOtherDay) },
  { label: "Weekly", value: String(NudgeFrequency.Weekly) },
];
const MAX_PER_DAY_OPTIONS: DropdownOption[] = [
  { label: "1", value: "1" },
  { label: "3", value: "3" },
  { label: "5", value: "5" },
  { label: "10", value: "10" },
  { label: "No Limit", value: NO_LIMIT_PER_DAY_VALUE },
];
const SMS_MAX_PER_DAY_OPTIONS: DropdownOption[] = [
  { label: "1", value: "1" },
  { label: "2", value: "2" },
  { label: "3", value: "3" },
  { label: "5", value: "5" },
];

export const EmailSmsPreferences = ({
  activeVerifiedSmsPhoneNumber,
  emailEnabled,
  textMessagesEnabled,
  smsConsent,
  smsPhoneNumber,
  smsCountryCode,
  isSmsPhoneVerified,
  onPhoneVerificationSuccess,
  frequencyValue,
  emailMaxPerDayValue,
  smsMaxPerDayValue,
  animationsEnabled,
  onUserInteraction,
  onEmailNotificationsToggle,
  onTextMessagesToggle,
  onSmsConsentChange,
  onFrequencyChange,
  onEmailMaxPerDayChange,
  onSmsMaxPerDayChange,
  onSmsPhoneNumberChange,
}: EmailSmsPreferencesProps) => {
  const [openDropdownKey, setOpenDropdownKey] = useState<"frequency" | "maxPerDay" | "smsMaxPerDay" | null>(null);
  const [emailPreferenceHeight, setEmailPreferenceHeight] = useState(0);
  const [smsPreferenceHeight, setSmsPreferenceHeight] = useState(0);
  const {
    OTP_LENGTH,
    codeSent,
    phoneVerified,
    otpValue,
    sendingCode,
    verifying,
    otpErrorMessage,
    countdown,
    otpInputIsFocused,
    showOtpSection,
    showConsentSection,
    showSendCodeAction,
    activeOtpDigitIndex,
    otpDigitGroups,
    otpInputRef,
    setSmsConsent,
    setOtpInputIsFocused,
    handleOtpChange,
    handleSendCodePress,
    handleResendPress,
    handleSmsPhoneNumberChange,
    focusOtpInput,
    resetOtpState,
  } = useOtpUiState({
    activeVerifiedPhoneNumber: activeVerifiedSmsPhoneNumber,
    onPhoneVerificationSuccess,
    smsConsent,
    onSmsConsentChange,
    smsPhoneNumber,
    smsCountryCode,
    initialPhoneVerified: isSmsPhoneVerified,
    onSmsPhoneNumberChange,
  });

  useEffect(() => {
    if (!emailEnabled && (openDropdownKey === "frequency" || openDropdownKey === "maxPerDay")) {
      setOpenDropdownKey(null);
    }
  }, [emailEnabled, openDropdownKey]);

  const enableAnimations = () => {
    onUserInteraction();
  };

  const handleDropdownOpenChange = (dropdownKey: "frequency" | "maxPerDay" | "smsMaxPerDay", isOpen: boolean) => {
    setOpenDropdownKey((previous) => {
      if (isOpen) {
        return dropdownKey;
      }

      return previous === dropdownKey ? null : previous;
    });
  };

  const handleSmsPreferenceHeightChange = (height: number) => {
    setSmsPreferenceHeight((previous) => {
      if (previous === height) {
        return previous;
      }

      return height;
    });
  };

  const handleEmailPreferenceHeightChange = (height: number) => {
    setEmailPreferenceHeight((previous) => {
      if (previous === height) {
        return previous;
      }

      return height;
    });
  };

  const emailPreferenceAnimatedHeight = emailEnabled ? emailPreferenceHeight : 0;
  const smsPreferenceAnimatedHeight = textMessagesEnabled ? smsPreferenceHeight : 0;
  const isFrequencyDropdownOpen = openDropdownKey === "frequency";
  const isMaxPerDayDropdownOpen = openDropdownKey === "maxPerDay";
  const isSmsMaxPerDayDropdownOpen = openDropdownKey === "smsMaxPerDay";
  const isEmailDropdownOpen = emailEnabled && (isFrequencyDropdownOpen || isMaxPerDayDropdownOpen);
  const isSectionOpen = isEmailDropdownOpen || isSmsMaxPerDayDropdownOpen;
  const resolvedEmailMaxPerDayValue = MAX_PER_DAY_OPTIONS.some((option) => option.value === emailMaxPerDayValue)
    ? emailMaxPerDayValue
    : NO_LIMIT_PER_DAY_VALUE;
  const resolvedSmsMaxPerDayValue = SMS_MAX_PER_DAY_OPTIONS.some((option) => option.value === smsMaxPerDayValue)
    ? smsMaxPerDayValue
    : SMS_MAX_PER_DAY_OPTIONS[0].value;
  const shouldAnimate = animationsEnabled;
  const submenuAnimationDuration = shouldAnimate ? SUBMENU_ANIMATION_DURATION_MS : 0;
  const inlineAnimationDuration = shouldAnimate ? 180 : 0;

  return (
    <View style={[styles.sectionContainer, isSectionOpen && styles.sectionContainerOpen]}>
      <View style={[styles.sectionHeader, styles.sectionHeaderWithBottomSpacing]}>
        <Mail size={18} color={colors.grey[500]} />
        <CustomText text="How to reach you" useTranslationText={false} style={styles.sectionHeaderTitle} />
      </View>

      <View style={styles.preferenceRow}>
        <View style={styles.preferenceTextContainer}>
          <CustomText text="Email notifications" useTranslationText={false} style={styles.preferenceTitle} />
          <CustomText text="Receive notifications via email" useTranslationText={false} style={styles.preferenceHint} />
        </View>
        <CustomSwitch
          value={emailEnabled}
          onValueChange={(nextValue) => {
            enableAnimations();
            onEmailNotificationsToggle(nextValue);
          }}
          accessibilityLabel="Email notifications"
        />
      </View>

      <MotiView
        animate={shouldAnimate ? { height: emailPreferenceAnimatedHeight } : undefined}
        transition={shouldAnimate ? { type: "timing", duration: submenuAnimationDuration } : undefined}
        style={[
          styles.preferenceDetailAnimatedContainer,
          !isEmailDropdownOpen && styles.preferenceDetailAnimatedContainerClipped,
          isSectionOpen && styles.preferenceDetailContainerOpen,
          !shouldAnimate && { height: emailPreferenceAnimatedHeight },
        ]}>
        <View
          style={styles.preferenceDetailContent}
          onLayout={(event) => {
            handleEmailPreferenceHeightChange(event.nativeEvent.layout.height);
          }}>
          <View style={[styles.selectFieldRow, isFrequencyDropdownOpen && styles.selectFieldRowOpen]}>
            <CustomText text="Frequency" useTranslationText={false} style={styles.selectLabel} />
            <CustomDropdown
              options={FREQUENCY_OPTIONS}
              value={frequencyValue}
              onChange={(nextValue) => {
                setOpenDropdownKey(null);
                enableAnimations();
                onFrequencyChange(nextValue);
              }}
              accessibilityLabel="Notification frequency"
              open={isFrequencyDropdownOpen}
              onOpenChange={(isOpen) => {
                enableAnimations();
                handleDropdownOpenChange("frequency", isOpen);
              }}
              containerStyle={[styles.selectDropdown, isFrequencyDropdownOpen && styles.selectDropdownOpen]}
              dropdownStyle={styles.selectDropdownField}
              dropdownContainerStyle={styles.selectDropdownMenu}
              hideItemBorders
            />
          </View>

          <View style={[styles.selectFieldRow, isMaxPerDayDropdownOpen && styles.selectFieldRowOpen]}>
            <CustomText text="Max per day" useTranslationText={false} style={styles.selectLabel} />
            <CustomDropdown
              options={MAX_PER_DAY_OPTIONS}
              value={resolvedEmailMaxPerDayValue}
              onChange={(nextValue) => {
                setOpenDropdownKey(null);
                enableAnimations();
                onEmailMaxPerDayChange(nextValue);
              }}
              accessibilityLabel="Max notifications per day"
              open={isMaxPerDayDropdownOpen}
              onOpenChange={(isOpen) => {
                enableAnimations();
                handleDropdownOpenChange("maxPerDay", isOpen);
              }}
              containerStyle={[styles.selectDropdown, isMaxPerDayDropdownOpen && styles.selectDropdownOpen]}
              dropdownStyle={styles.selectDropdownField}
              dropdownContainerStyle={styles.selectDropdownMenu}
              hideItemBorders
            />
          </View>
        </View>
      </MotiView>

      <View style={styles.sectionDivider} />

      <View style={styles.preferenceRow}>
        <View style={styles.preferenceTextContainer}>
          <View style={styles.inlineHeader}>
            <MessageSquareText size={16} color={colors.grey[500]} />
            <CustomText text="Text messages" useTranslationText={false} style={styles.preferenceTitle} />
          </View>
          <CustomText text="Get urgent reminders via SMS" useTranslationText={false} style={styles.preferenceHint} />
        </View>
        <CustomSwitch
          value={textMessagesEnabled}
          onValueChange={(nextValue) => {
            enableAnimations();
            onTextMessagesToggle(nextValue);

            if (!nextValue) {
              setOpenDropdownKey(null);

              if (phoneVerified) {
                setSmsConsent(false);
                return;
              }

              resetOtpState();
            }
          }}
          accessibilityLabel="Text message notifications"
        />
      </View>

      <MotiView
        animate={shouldAnimate ? { height: smsPreferenceAnimatedHeight } : undefined}
        transition={shouldAnimate ? { type: "timing", duration: submenuAnimationDuration } : undefined}
        style={[
          styles.preferenceDetailAnimatedContainer,
          !isSmsMaxPerDayDropdownOpen && styles.preferenceDetailAnimatedContainerClipped,
          isEmailDropdownOpen && styles.preferenceDetailContainerBehindOpenDropdown,
          !shouldAnimate && { height: smsPreferenceAnimatedHeight },
        ]}>
        <View
          style={styles.preferenceDetailContent}
          onLayout={(event) => {
            handleSmsPreferenceHeightChange(event.nativeEvent.layout.height);
          }}>
          <View style={styles.phoneFieldRow}>
            <CustomText text="Phone number" useTranslationText={false} style={styles.phoneFieldLabel} />
            <View style={styles.phoneInputRow}>
              <View style={styles.phoneInputContainer}>
                <CustomTextInput
                  value={smsPhoneNumber}
                  onChangeText={handleSmsPhoneNumberChange}
                  placeholder="(555) 555-5555"
                  keyboardType="phone-pad"
                  autoComplete="tel"
                  textContentType="telephoneNumber"
                  accessibilityLabel="SMS phone number"
                  style={styles.phoneInput}
                />
              </View>

              {phoneVerified && !codeSent && smsPhoneNumber.length > 0 ? (
                <View style={styles.verifiedBadge}>
                  <CheckCircle2 size={14} color={colors.success[700]} />
                  <CustomText text="Verified" useTranslationText={false} style={styles.verifiedBadgeText} />
                </View>
              ) : null}

              {showSendCodeAction ? (
                <Pressable
                  disabled={sendingCode || verifying}
                  accessibilityRole="button"
                  accessibilityLabel="Send SMS verification code"
                  onPress={() => {
                    enableAnimations();
                    void handleSendCodePress();
                  }}
                  style={[styles.sendCodeButton, (sendingCode || verifying) && styles.sendCodeButtonDisabled]}>
                  <CustomText text="Send code" useTranslationText={false} style={styles.sendCodeButtonText} />
                </Pressable>
              ) : null}
            </View>

            {showOtpSection ? (
              <MotiView
                from={{ opacity: 0, translateY: -4 }}
                animate={{ opacity: 1, translateY: 0 }}
                transition={{
                  type: "timing",
                  duration: inlineAnimationDuration,
                }}
                style={styles.otpSectionContainer}>
                <CustomText
                  text="Enter the 6-digit code sent to your phone"
                  useTranslationText={false}
                  style={styles.otpHelperText}
                />
                <View style={styles.otpInputContainer}>
                  <TextInput
                    ref={otpInputRef}
                    value={otpValue}
                    onChangeText={handleOtpChange}
                    onFocus={() => {
                      setOtpInputIsFocused(true);
                    }}
                    onBlur={() => {
                      setOtpInputIsFocused(false);
                    }}
                    keyboardType="number-pad"
                    maxLength={OTP_LENGTH}
                    editable={!verifying}
                    autoFocus={showOtpSection}
                    accessibilityLabel="SMS verification code"
                    style={styles.otpHiddenInput}
                  />
                  <View
                    accessibilityRole="text"
                    accessibilityLabel="Enter SMS verification code"
                    onStartShouldSetResponder={() => !verifying}
                    onResponderRelease={focusOtpInput}
                    style={styles.otpGroupedInputPressable}>
                    {otpDigitGroups.map((group, groupIndex) => (
                      <Fragment key={`otp-group-${groupIndex}`}>
                        <View style={styles.otpDigitGroup}>
                          {group.map((digitIndex, index) => {
                            const isActiveDigit = otpInputIsFocused && activeOtpDigitIndex === digitIndex;
                            const showCaret = isActiveDigit && !otpValue[digitIndex];

                            return (
                              <View key={`otp-cell-${groupIndex}-${index}`} style={styles.otpDigitCellWrapper}>
                                <View
                                  style={[
                                    styles.otpDigitCell,
                                    index === 0 && styles.otpDigitCellFirst,
                                    index === 2 && styles.otpDigitCellLast,
                                    isActiveDigit && styles.otpDigitCellActive,
                                  ]}>
                                  <CustomText
                                    text={otpValue[digitIndex] ?? ""}
                                    useTranslationText={false}
                                    style={styles.otpDigitText}
                                  />
                                  {showCaret ? <View style={styles.otpDigitCaret} /> : null}
                                </View>
                                {index < 2 ? <View style={styles.otpDigitDivider} /> : null}
                              </View>
                            );
                          })}
                        </View>
                        {groupIndex === 0 ? (
                          <CustomText text="•" useTranslationText={false} style={styles.otpGroupSeparator} />
                        ) : null}
                      </Fragment>
                    ))}
                  </View>
                </View>
                {otpErrorMessage ? (
                  <CustomText text={otpErrorMessage} useTranslationText={false} style={styles.otpErrorText} />
                ) : null}
                {verifying ? (
                  <CustomText text="Verifying..." useTranslationText={false} style={styles.otpStatusText} />
                ) : sendingCode ? (
                  <CustomText text="Sending code..." useTranslationText={false} style={styles.otpStatusText} />
                ) : countdown > 0 && !otpErrorMessage ? (
                  <CustomText
                    text={`Resend in 0:${countdown.toString().padStart(2, "0")}`}
                    useTranslationText={false}
                    style={styles.otpStatusText}
                  />
                ) : (
                  <View style={styles.resendActionContainer}>
                    <CustomText text="Didn't get it?" useTranslationText={false} style={styles.otpStatusText} />
                    <Pressable
                      disabled={sendingCode || verifying}
                      accessibilityRole="button"
                      accessibilityLabel="Resend SMS verification code"
                      onPress={() => {
                        enableAnimations();
                        void handleResendPress();
                      }}>
                      <CustomText
                        text="Resend"
                        useTranslationText={false}
                        style={[styles.resendActionText, (sendingCode || verifying) && styles.resendActionTextDisabled]}
                      />
                    </Pressable>
                  </View>
                )}
              </MotiView>
            ) : null}

            {showConsentSection ? (
              <MotiView
                from={{ opacity: 0, translateY: -4 }}
                animate={{ opacity: 1, translateY: 0 }}
                transition={{
                  type: "timing",
                  duration: inlineAnimationDuration,
                }}
                style={styles.smsConsentSectionContainer}>
                <View style={[styles.smsMaxPerDayRow, isSmsMaxPerDayDropdownOpen && styles.selectFieldRowOpen]}>
                  <CustomText text="Max per day" useTranslationText={false} style={styles.smsMaxPerDayLabel} />
                  <CustomDropdown
                    options={SMS_MAX_PER_DAY_OPTIONS}
                    value={resolvedSmsMaxPerDayValue}
                    onChange={(nextValue) => {
                      setOpenDropdownKey(null);
                      enableAnimations();
                      onSmsMaxPerDayChange(nextValue);
                    }}
                    accessibilityLabel="Max SMS notifications per day"
                    open={isSmsMaxPerDayDropdownOpen}
                    onOpenChange={(isOpen) => {
                      enableAnimations();
                      handleDropdownOpenChange("smsMaxPerDay", isOpen);
                    }}
                    containerStyle={[
                      styles.smsMaxPerDayDropdown,
                      isSmsMaxPerDayDropdownOpen && styles.selectDropdownOpen,
                    ]}
                    dropdownStyle={styles.smsMaxPerDayDropdownField}
                    dropdownContainerStyle={styles.smsMaxPerDayDropdownMenu}
                    hideItemBorders
                  />
                </View>
                <View style={styles.smsConsentRow}>
                  <CustomSwitch
                    value={smsConsent}
                    onValueChange={(nextValue) => {
                      enableAnimations();
                      setSmsConsent(nextValue);
                    }}
                    accessibilityLabel="SMS consent"
                    style={styles.smsConsentSwitch}
                  />
                  <View style={styles.smsConsentTextContainer}>
                    <CustomText
                      text="I consent to receive SMS messages from Axio"
                      useTranslationText={false}
                      style={styles.smsConsentTitle}
                    />
                    <CustomText
                      text="Message and data rates may apply. Reply STOP to unsubscribe."
                      useTranslationText={false}
                      style={styles.smsConsentHint}
                    />
                  </View>
                </View>
              </MotiView>
            ) : null}
          </View>
        </View>
      </MotiView>
    </View>
  );
};

const styles = StyleSheet.create({
  sectionContainer: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.lg,
    padding: spacing.lg,
    position: "relative",
    overflow: "visible",
  },
  sectionContainerOpen: {
    zIndex: 15000,
    elevation: 15000,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  sectionHeaderWithBottomSpacing: {
    marginBottom: spacing.md,
  },
  sectionHeaderTitle: {
    fontSize: typographyScale.textBase.fontSize,
    lineHeight: typographyScale.textBase.lineHeight,
    fontWeight: "600",
    color: colors.grey[800],
  },
  preferenceRow: {
    marginTop: spacing.sm,
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: spacing.sm,
  },
  preferenceTextContainer: {
    flex: 1,
    gap: spacing.xxs,
  },
  preferenceTitle: {
    color: colors.grey[800],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
  },
  preferenceHint: {
    color: colors.grey[500],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  preferenceDetailAnimatedContainer: {
    position: "relative",
    overflow: "visible",
  },
  preferenceDetailContainerOpen: {
    zIndex: 1,
    elevation: 1,
  },
  preferenceDetailContainerBehindOpenDropdown: {
    zIndex: 0,
    elevation: 0,
  },
  preferenceDetailAnimatedContainerClipped: {
    overflow: "hidden",
  },
  preferenceDetailContent: {
    overflow: "visible",
  },
  selectFieldRow: {
    marginTop: spacing.sm,
    maxWidth: 320,
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.sm,
    position: "relative",
    overflow: "visible",
  },
  selectFieldRowOpen: {
    zIndex: 16000,
    elevation: 16000,
  },
  selectLabel: {
    color: colors.grey[600],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    width: 88,
  },
  selectDropdown: {
    flex: 1,
    position: "relative",
    overflow: "visible",
  },
  selectDropdownOpen: {
    zIndex: 17000,
    elevation: 17000,
  },
  selectDropdownField: {
    height: 30,
    width: NOTIFICATION_DROPDOWN_WIDTH,
    borderColor: colors.grey[300],
    backgroundColor: colors.white,
    borderRadius: radii.md,
  },
  selectDropdownMenu: {
    width: NOTIFICATION_DROPDOWN_WIDTH,
    borderColor: colors.grey[300],
    backgroundColor: colors.white,
  },
  sectionDivider: {
    height: 1,
    backgroundColor: colors.grey[200],
    marginTop: spacing.md,
    marginBottom: spacing.md,
  },
  inlineHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  phoneFieldRow: {
    marginTop: spacing.sm,
    width: "100%",
    backgroundColor: colors.grey[50],
    borderRadius: radii.xl,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    alignItems: "flex-start",
    gap: spacing.xs,
  },
  phoneFieldLabel: {
    color: colors.grey[800],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "400",
  },
  phoneInputContainer: {
    flex: 1,
    maxWidth: 200,
  },
  phoneInputRow: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.xs,
  },
  phoneInput: {
    minHeight: 40,
    borderColor: colors.grey[300],
    borderRadius: radii.md,
    backgroundColor: colors.white,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  sendCodeButton: {
    borderWidth: 1,
    borderColor: colors.brand[200],
    backgroundColor: colors.brand[50],
    borderRadius: radii.md,
    minWidth: 80,
    minHeight: 32,
    paddingVertical: spacing.xxs,
    paddingHorizontal: spacing.sm,
    justifyContent: "center",
    alignItems: "center",
  },
  sendCodeButtonDisabled: {
    opacity: 0.55,
  },
  sendCodeButtonText: {
    color: colors.brand[700],
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: "600",
  },
  verifiedBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
    backgroundColor: colors.success[50],
    borderRadius: radii.full,
    minHeight: 28,
    paddingVertical: 3,
    paddingHorizontal: 10,
  },
  verifiedBadgeText: {
    color: colors.success[700],
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: "600",
  },
  otpSectionContainer: {
    paddingTop: spacing.xs,
    gap: spacing.xs,
    width: "100%",
  },
  otpHelperText: {
    color: colors.grey[500],
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
  },
  otpInputContainer: {
    width: "100%",
  },
  otpHiddenInput: {
    position: "absolute",
    opacity: 0,
    width: 1,
    height: 1,
  },
  otpGroupedInputPressable: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
  },
  otpDigitGroup: {
    flexDirection: "row",
    alignItems: "center",
    width: 120,
    height: 40,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: radii.md,
    overflow: "hidden",
    backgroundColor: colors.grey[50],
  },
  otpDigitCell: {
    flex: 1,
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
  otpDigitCellFirst: {
    borderTopLeftRadius: radii.md,
    borderBottomLeftRadius: radii.md,
  },
  otpDigitCellLast: {
    borderTopRightRadius: radii.md,
    borderBottomRightRadius: radii.md,
  },
  otpDigitCellWrapper: {
    flex: 1,
    height: "100%",
    flexDirection: "row",
    alignItems: "stretch",
  },
  otpDigitDivider: {
    width: 1,
    height: "100%",
    backgroundColor: colors.grey[300],
  },
  otpDigitCellActive: {
    borderWidth: 1,
    borderColor: colors.brand[500],
    backgroundColor: colors.white,
  },
  otpDigitText: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
  },
  otpDigitCaret: {
    position: "absolute",
    width: 1,
    height: spacing.sm,
    backgroundColor: colors.grey[700],
  },
  otpGroupSeparator: {
    marginHorizontal: spacing.sm,
    color: colors.grey[600],
    fontSize: typographyScale.textBase.fontSize,
    lineHeight: typographyScale.textBase.lineHeight,
  },
  otpStatusText: {
    color: colors.grey[500],
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
  },
  otpErrorText: {
    color: colors.error[600],
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
  },
  resendActionContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
  },
  resendActionText: {
    color: colors.brand[600],
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: "600",
  },
  resendActionTextDisabled: {
    color: colors.grey[400],
  },
  smsConsentSectionContainer: {
    paddingTop: spacing.xxs,
    gap: spacing.xs,
    width: "100%",
    overflow: "visible",
  },
  smsMaxPerDayRow: {
    width: 208,
    maxWidth: "100%",
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    position: "relative",
    overflow: "visible",
  },
  smsMaxPerDayLabel: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    width: 72,
  },
  smsMaxPerDayDropdown: {
    width: 84,
    position: "relative",
    overflow: "visible",
  },
  smsMaxPerDayDropdownField: {
    height: 32,
    width: 84,
    borderColor: colors.grey[300],
    backgroundColor: colors.white,
    borderRadius: radii.md,
  },
  smsMaxPerDayDropdownMenu: {
    width: 84,
    borderColor: colors.grey[300],
    backgroundColor: colors.white,
  },
  smsConsentRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: spacing.xs,
  },
  smsConsentSwitch: {
    marginTop: spacing.xxs,
  },
  smsConsentTextContainer: {
    flex: 1,
    gap: spacing.xxs,
  },
  smsConsentTitle: {
    color: colors.grey[800],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
  },
  smsConsentHint: {
    color: colors.grey[500],
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
  },
});
